import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { makeChecklistStorageKey, type ChecklistData } from '../../lib/interactiveChecklist';

interface InteractiveChecklistProps {
  /** Post slug — used to namespace the localStorage key. */
  slug: string;
  /** Parsed checklist data from the markdown block. */
  data: ChecklistData;
  /**
   * Zero-based index across all checklists in this article.
   * The first checklist gets `id="checklist"`, subsequent ones
   * get `id="checklist-1"`, `id="checklist-2"`, etc.
   */
  anchorIndex: number;
  className?: string;
}

type CheckedMap = Record<string, boolean>;

function makeItemId(sectionIdx: number, itemIdx: number): string {
  return `${sectionIdx}-${itemIdx}`;
}

const InteractiveChecklist: React.FC<InteractiveChecklistProps> = ({
  slug,
  data,
  anchorIndex,
  className = '',
}) => {
  const anchorId = anchorIndex === 0 ? 'checklist' : `checklist-${anchorIndex}`;

  // Flat list of all items for progress calculations
  const allItemIds = useMemo(() => {
    const ids: string[] = [];
    data.sections.forEach((sec, si) => {
      sec.items.forEach((_, ii) => ids.push(makeItemId(si, ii)));
    });
    return ids;
  }, [data]);

  const totalItems = allItemIds.length;

  const storageKey = useMemo(
    () => makeChecklistStorageKey(slug, data.title, anchorIndex),
    [slug, data.title, anchorIndex]
  );

  const [checked, setChecked] = useState<CheckedMap>({});

  // Hydrate from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (!stored) return;
      const parsed: unknown = JSON.parse(stored);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setChecked(parsed as CheckedMap);
      }
    } catch {
      // localStorage unavailable or invalid JSON — start fresh
    }
  }, [storageKey]);

  const checkedCount = useMemo(
    () => allItemIds.filter((id) => checked[id]).length,
    [allItemIds, checked]
  );

  const progressPct = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
  const isComplete = totalItems > 0 && checkedCount === totalItems;

  const persist = (next: CheckedMap) => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      }
    } catch {
      // silent
    }
  };

  const toggleItem = (id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persist(next);
      return next;
    });
  };

  const resetAll = () => {
    setChecked({});
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(storageKey);
      }
    } catch {
      // silent
    }
  };

  return (
    <section
      id={anchorId}
      className={`rounded-2xl border p-5 sm:p-6 transition-colors duration-500 ${
        isComplete
          ? 'border-emerald-400/40 bg-emerald-500/5'
          : 'border-slate-800 bg-slate-900/70'
      } ${className}`}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-lg sm:text-xl font-semibold text-white">
          {data.title ?? 'Checklist'}
        </h2>

        {checkedCount > 0 && (
          <button
            type="button"
            onClick={resetAll}
            title="Resetar progresso"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <RotateCcw className="h-3 w-3" strokeWidth={2} />
            Resetar
          </button>
        )}
      </div>

      {/* ── Progress ─────────────────────────────────���──────────────────── */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
          <span>
            {checkedCount}/{totalItems}{' '}
            {isComplete ? (
              <span className="text-emerald-400 font-medium">✅ Tudo concluído!</span>
            ) : (
              'itens verificados'
            )}
          </span>
          <span className="tabular-nums">{progressPct}%</span>
        </div>

        <div className="h-1.5 rounded-full overflow-hidden bg-slate-800">
          <div
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            className={`h-full rounded-full transition-all duration-500 ${
              isComplete ? 'bg-emerald-400' : 'bg-amber-400'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* ── Sections & Items ─────────────────────────────────────────────── */}
      <div className="space-y-5">
        {data.sections.map((section, si) => (
          <div key={si}>
            {/* Group heading */}
            {section.group && (
              <p className="font-display text-xs font-bold text-slate-400 uppercase tracking-widest mb-2.5 pb-1.5 border-b border-slate-800">
                {section.group}
              </p>
            )}

            <ul className="space-y-2">
              {section.items.map((item, ii) => {
                const id = makeItemId(si, ii);
                const isChecked = checked[id] === true;

                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => toggleItem(id)}
                      className="flex items-start gap-3 w-full text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-lg"
                    >
                      {/* Custom checkbox */}
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                          isChecked
                            ? 'bg-emerald-500/20 border-emerald-400/70'
                            : 'bg-slate-800 border-slate-600 group-hover:border-amber-400/60'
                        }`}
                      >
                        {isChecked && (
                          <svg
                            className="w-3 h-3 text-emerald-400"
                            viewBox="0 0 12 12"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2 6l3 3 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>

                      {/* Item text */}
                      <span
                        className={`text-sm sm:text-base leading-relaxed transition-all duration-200 ${
                          isChecked
                            ? 'line-through text-slate-500'
                            : 'text-slate-200 group-hover:text-white'
                        }`}
                      >
                        {item}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default InteractiveChecklist;


